import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { SurveyData } from "./surveysApi";

/**
 * Public API - for endpoints that don't require authentication
 * This API does NOT add auth headers or instanceId query params
 */
export const publicApi = createApi({
  reducerPath: "publicApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1",
  }),
  endpoints: (builder) => ({
    // ============================================
    // Public Survey Endpoints (no auth required)
    // ============================================

    getPublicSurvey: builder.query<
      {
        survey_id: string;
        title: string;
        description?: string;
        survey_data: SurveyData;
        survey_token: string;
        customer_name?: string;
      },
      { instanceId: string; token: string }
    >({
      // POST, and the token rides in the BODY — never in the URL. As a path
      // segment a 256-bit bearer token gets written into proxy/access logs, APM
      // traces and the `Referer` header. Same shape as the public unsubscribe
      // (server/routes/emailUnsubscribe.ts), which POSTs its token for the same
      // reason. `builder.query` is still correct here: this is a read, and the
      // page wants query caching/loading state, not mutation semantics.
      //
      // The server also still serves the old `GET .../:token` so that survey
      // invitations already in inboxes keep working — but nothing should call it.
      query: ({ instanceId, token }) => ({
        url: `/surveys/public/${instanceId}/resolve`,
        method: "POST",
        body: { token },
      }),
    }),

    submitPublicSurveyResponse: builder.mutation<
      { id: string; message: string },
      { instanceId: string; token: string; response_data: Record<string, unknown> }
    >({
      query: ({ instanceId, token, response_data }) => ({
        url: `/surveys/public/${instanceId}/submit`,
        method: "POST",
        body: { token, response_data },
      }),
    }),

    // ============================================
    // Public Community-Event RSVP (no auth required)
    // ============================================
    //
    // The URL carries a bearer credential (`token`). RTK Query caches by
    // serialised args, so the token is already in the Redux store on this page
    // — that is inherent to the flow and matches the public survey endpoints
    // above. What must NOT happen is the token reaching anything durable: no
    // analytics call, no logger, no localStorage. Keep it to these two hooks.

    getPublicEventRsvp: builder.query<
      {
        event_name: string;
        when: string;
        where: string;
        guest_name: string | null;
        rsvp_status: string;
      },
      { instanceId: string; token: string }
    >({
      query: ({ instanceId, token }) => `/public/community-events/${instanceId}/${token}`,
    }),

    submitPublicEventRsvp: builder.mutation<
      { rsvp_status: string; event_name: string },
      { instanceId: string; token: string; response: "accepted" | "declined" | "maybe" }
    >({
      query: ({ instanceId, token, response }) => ({
        url: `/public/community-events/${instanceId}/${token}/rsvp`,
        method: "POST",
        body: { response },
      }),
    }),
  }),
});

export const {
  useGetPublicSurveyQuery,
  useSubmitPublicSurveyResponseMutation,
  useGetPublicEventRsvpQuery,
  useSubmitPublicEventRsvpMutation,
} = publicApi;
